/**
 * Rent-vs-buy calculator app shell.
 *
 * The whole page is rendered by this one module: hero, live results view
 * (headline break-even year + net-worth chart + per-group explanations from
 * ui/results.ts), six primary inputs above the fold, and the collapsible
 * "show defaults" panel with the remaining assumptions. State lives in
 * memory *and* in the URL query string — every edit re-encodes the URL via
 * history.replaceState so a share/refresh preserves the scenario. Recompute
 * is synchronous and happens on every input event, so there is no
 * perceptible lag on drag or keystroke.
 */

import { trackEvent } from "../analytics";
import {
  SCENARIO_SOURCES,
  type NumericScenarioKey,
  type ScenarioInputs,
  type SourceCitation,
} from "../defaults";
import { JURISDICTIONS, getJurisdiction } from "../math/jurisdictions";
import { DEFAULT_SOURCES } from "../math/mortgage";
import { encodeScenarioToQuery, parseScenarioFromQuery } from "../state";
import { renderResults, updateResults } from "./results";

interface NumericFieldSpec {
  readonly key: NumericScenarioKey;
  readonly label: string;
  readonly prefix?: "$";
  readonly suffix?: string;
  readonly step: number;
  readonly min: number;
  readonly max: number;
  readonly source: SourceCitation;
}

const PRIMARY_FIELDS: readonly NumericFieldSpec[] = [
  {
    key: "monthlyRent",
    label: "Monthly rent",
    prefix: "$",
    suffix: "/mo",
    step: 50,
    min: 0,
    max: 20_000,
    source: SCENARIO_SOURCES.monthlyRent,
  },
  {
    key: "homePrice",
    label: "Home price",
    prefix: "$",
    step: 5_000,
    min: 0,
    max: 10_000_000,
    source: SCENARIO_SOURCES.homePrice,
  },
  {
    key: "downPaymentPercent",
    label: "Down payment",
    suffix: "%",
    step: 0.5,
    min: 0,
    max: 100,
    source: DEFAULT_SOURCES.downPaymentPercent,
  },
  {
    key: "annualInterestRatePercent",
    label: "Mortgage rate",
    suffix: "%",
    step: 0.1,
    min: 0,
    max: 25,
    source: DEFAULT_SOURCES.annualInterestRatePercent,
  },
  {
    key: "horizonYears",
    label: "Years staying",
    suffix: "yr",
    step: 1,
    min: 1,
    max: 40,
    source: SCENARIO_SOURCES.horizonYears,
  },
  {
    key: "annualHomeAppreciationPercent",
    label: "Home appreciation",
    suffix: "%/yr",
    step: 0.1,
    min: -10,
    max: 25,
    source: SCENARIO_SOURCES.annualHomeAppreciationPercent,
  },
];

const DEFAULT_FIELDS: readonly NumericFieldSpec[] = [
  {
    key: "termYears",
    label: "Mortgage term",
    suffix: "yr",
    step: 1,
    min: 1,
    max: 40,
    source: DEFAULT_SOURCES.termYears,
  },
  {
    key: "annualRentInflationPercent",
    label: "Rent inflation",
    suffix: "%/yr",
    step: 0.1,
    min: 0,
    max: 20,
    source: SCENARIO_SOURCES.annualRentInflationPercent,
  },
  {
    key: "annualDiscountRatePercent",
    label: "Investment return",
    suffix: "%/yr",
    step: 0.1,
    min: 0,
    max: 20,
    source: SCENARIO_SOURCES.annualDiscountRatePercent,
  },
  {
    key: "annualPropertyTaxRatePercent",
    label: "Property tax rate",
    suffix: "%/yr",
    step: 0.05,
    min: 0,
    max: 5,
    source: SCENARIO_SOURCES.annualPropertyTaxRatePercent,
  },
  {
    key: "annualHomeInsurance",
    label: "Home insurance",
    prefix: "$",
    suffix: "/yr",
    step: 50,
    min: 0,
    max: 20_000,
    source: SCENARIO_SOURCES.annualHomeInsurance,
  },
  {
    key: "annualMaintenanceRatePercent",
    label: "Maintenance",
    suffix: "%/yr",
    step: 0.1,
    min: 0,
    max: 5,
    source: SCENARIO_SOURCES.annualMaintenanceRatePercent,
  },
  {
    key: "monthlyHOA",
    label: "HOA / condo fees",
    prefix: "$",
    suffix: "/mo",
    step: 10,
    min: 0,
    max: 3_000,
    source: SCENARIO_SOURCES.monthlyHOA,
  },
  {
    key: "annualRentersInsurance",
    label: "Renter's insurance",
    prefix: "$",
    suffix: "/yr",
    step: 10,
    min: 0,
    max: 2_000,
    source: SCENARIO_SOURCES.annualRentersInsurance,
  },
];

/**
 * Build and mount the app into the container. Returns nothing — the app
 * runs for the lifetime of the page.
 */
export function mountApp(container: HTMLElement): void {
  const state: ScenarioInputs = parseScenarioFromQuery(
    typeof window !== "undefined" ? window.location.search : "",
  );

  container.innerHTML = "";
  container.append(renderSkipLink());
  container.append(renderHero());
  const results = renderResults();
  container.append(results.root);
  const rerender = () => {
    updateResults(results, state);
    updateURL(state);
    // First edit means the user made this scenario their own — that's the
    // completion signal we care about (not the page-load default view).
    trackEvent("complete");
  };
  const form = renderForm(state, rerender, () => {
    // Reset re-parses from the URL, so wipe the query first — otherwise
    // shared/tweaked URLs would silently defeat the button.
    if (typeof window !== "undefined") {
      window.history.replaceState(null, "", window.location.pathname);
    }
    mountApp(container);
  });
  container.append(form);
  container.append(renderFootnote());

  updateResults(results, state);
}

function renderHero(): HTMLElement {
  const hero = document.createElement("header");
  hero.className = "hero";
  hero.innerHTML = `
    <h1>Should you rent or buy?</h1>
    <p>Enter your numbers. Every default is cited. Nothing you type leaves your browser.</p>
  `;
  return hero;
}

/**
 * Keyboard-only skip link — REN-16. Lets a keyboard user jump past the hero
 * and results straight to the primary input form without tabbing through the
 * whole answer card. Visible only on focus.
 */
function renderSkipLink(): HTMLElement {
  const link = document.createElement("a");
  link.className = "skip-link";
  link.href = "#scenario-form";
  link.textContent = "Skip to inputs";
  return link;
}

function updateURL(state: ScenarioInputs): void {
  if (typeof window === "undefined") return;
  const query = encodeScenarioToQuery(state);
  const url = query ? `${window.location.pathname}?${query}` : window.location.pathname;
  window.history.replaceState(null, "", url);
}

function renderForm(
  state: ScenarioInputs,
  onChange: () => void,
  onReset: () => void,
): HTMLElement {
  const form = document.createElement("form");
  form.id = "scenario-form";
  form.className = "form";
  form.noValidate = true;
  form.setAttribute("aria-label", "Your rent-vs-buy scenario");
  form.addEventListener("submit", (event) => event.preventDefault());

  const primary = document.createElement("fieldset");
  primary.append(legend("Your scenario"));
  for (const spec of PRIMARY_FIELDS) {
    primary.append(renderField(spec, state, onChange));
  }
  form.append(primary);

  const toggle = document.createElement("button");
  toggle.type = "button";
  toggle.className = "defaults-toggle";
  toggle.setAttribute("aria-expanded", "false");
  toggle.setAttribute("aria-controls", "defaults-panel");
  toggle.innerHTML = `<span class="chevron" aria-hidden="true">›</span> Show every default`;

  const panel = document.createElement("div");
  panel.id = "defaults-panel";
  panel.className = "defaults-panel";
  panel.hidden = true;

  const defaultsSet = document.createElement("fieldset");
  defaultsSet.append(legend("Assumptions"));
  for (const spec of DEFAULT_FIELDS) {
    defaultsSet.append(renderField(spec, state, onChange));
  }
  defaultsSet.append(renderJurisdiction(state, onChange));
  defaultsSet.append(renderNeverSellToggle(state, onChange));
  panel.append(defaultsSet);

  toggle.addEventListener("click", () => {
    const expanded = toggle.getAttribute("aria-expanded") === "true";
    toggle.setAttribute("aria-expanded", String(!expanded));
    panel.hidden = expanded;
    toggle.innerHTML = expanded
      ? `<span class="chevron" aria-hidden="true">›</span> Show every default`
      : `<span class="chevron" aria-hidden="true">›</span> Hide defaults`;
  });

  form.append(toggle);
  form.append(panel);
  form.append(renderActions(onReset));
  return form;
}

function legend(text: string): HTMLElement {
  const el = document.createElement("legend");
  el.textContent = text;
  return el;
}

function renderField(
  spec: NumericFieldSpec,
  state: ScenarioInputs,
  onChange: () => void,
): HTMLElement {
  const wrapper = document.createElement("div");
  wrapper.className = "field";

  const labelEl = document.createElement("label");
  const inputId = `field-${spec.key}`;
  labelEl.htmlFor = inputId;

  const labelRow = document.createElement("span");
  labelRow.className = "field-label";
  const labelText = document.createElement("span");
  labelText.textContent = spec.label;
  labelRow.append(labelText);
  labelEl.append(labelRow);
  wrapper.append(labelEl);

  const row = document.createElement("span");
  row.className = "input-row";

  if (spec.prefix) {
    const prefix = document.createElement("span");
    prefix.className = "input-affix";
    prefix.setAttribute("aria-hidden", "true");
    prefix.textContent = spec.prefix;
    row.append(prefix);
  }

  const input = document.createElement("input");
  input.type = "number";
  input.inputMode = "decimal";
  input.id = inputId;
  input.step = String(spec.step);
  input.min = String(spec.min);
  input.max = String(spec.max);
  input.value = String(state[spec.key]);
  input.name = spec.key;
  // The visible <label for> above already names the input; setting
  // aria-label here would silently override that visible name for screen
  // readers (a known WCAG "accessible name mismatches visible label" gap).
  input.addEventListener("input", () => {
    const parsed = Number(input.value);
    if (!Number.isFinite(parsed)) return;
    state[spec.key] = parsed;
    onChange();
  });

  row.append(input);

  if (spec.suffix) {
    const suffix = document.createElement("span");
    suffix.className = "input-affix";
    suffix.setAttribute("aria-hidden", "true");
    suffix.textContent = spec.suffix;
    row.append(suffix);
  }

  wrapper.append(row);
  wrapper.append(renderCitation([spec.source], inputId));
  return wrapper;
}

/**
 * Tap-to-open disclosure showing the source citation(s) for a field. The
 * summary reads "Source: <label>" so the origin is visible before opening;
 * the body reveals the `note` (why we picked this default) and a link to
 * the underlying dataset. Uses a native `<details>` so it degrades to a
 * usable expander with no JS and works identically on touch and pointer.
 */
function renderCitation(
  sources: readonly SourceCitation[],
  forId: string,
): HTMLElement {
  const details = document.createElement("details");
  details.className = "field-source";
  const summary = document.createElement("summary");
  const primary = sources[0];
  const heading = sources.length === 1
    ? `Source: ${primary?.label ?? "cited"}`
    : `Sources: ${sources.length} cited`;
  summary.textContent = heading;
  summary.setAttribute("aria-controls", `${forId}-src`);
  details.append(summary);

  const body = document.createElement("div");
  body.className = "field-source-body";
  body.id = `${forId}-src`;
  for (const source of sources) {
    const item = document.createElement("p");
    item.className = "field-source-item";
    const label = document.createElement("strong");
    label.textContent = source.label;
    const note = document.createElement("span");
    note.textContent = source.url ? ` — ${source.note} ` : ` — ${source.note}`;
    item.append(label, note);
    if (source.url) {
      const link = document.createElement("a");
      link.href = source.url;
      link.textContent = "View source";
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      item.append(link);
    }
    body.append(item);
  }
  details.append(body);
  return details;
}

function renderJurisdiction(
  state: ScenarioInputs,
  onChange: () => void,
): HTMLElement {
  const wrapper = document.createElement("div");
  wrapper.className = "field";

  const labelEl = document.createElement("label");
  labelEl.htmlFor = "field-jurisdictionId";
  const labelRow = document.createElement("span");
  labelRow.className = "field-label";
  const labelText = document.createElement("span");
  labelText.textContent = "Jurisdiction";
  labelRow.append(labelText);
  labelEl.append(labelRow);
  wrapper.append(labelEl);

  const select = document.createElement("select");
  select.id = "field-jurisdictionId";
  select.name = "jurisdictionId";
  for (const j of JURISDICTIONS) {
    const option = document.createElement("option");
    option.value = j.id;
    option.textContent = j.name;
    if (j.id === state.jurisdictionId) option.selected = true;
    select.append(option);
  }

  let citationEl = renderJurisdictionCitation(state.jurisdictionId);
  select.addEventListener("change", () => {
    state.jurisdictionId = select.value;
    const next = renderJurisdictionCitation(state.jurisdictionId);
    citationEl.replaceWith(next);
    citationEl = next;
    onChange();
  });
  wrapper.append(select);
  wrapper.append(citationEl);
  return wrapper;
}

/**
 * The selected jurisdiction drives three separate rates (closing costs,
 * transfer tax, selling commission) — surface all three source citations
 * so the user can audit each rate on its own.
 */
function renderJurisdictionCitation(jurisdictionId: string): HTMLElement {
  const jurisdiction = getJurisdiction(jurisdictionId);
  if (!jurisdiction) {
    const fallback = document.createElement("div");
    fallback.className = "field-source";
    return fallback;
  }
  const sources: SourceCitation[] = [
    {
      label: `${jurisdiction.name} — buyer closing costs`,
      url: "",
      note: jurisdiction.sources.buyerClosingCosts,
    },
    {
      label: `${jurisdiction.name} — transfer / stamp duty`,
      url: "",
      note: jurisdiction.sources.transferTaxOnPurchase,
    },
    {
      label: `${jurisdiction.name} — selling commission`,
      url: "",
      note: jurisdiction.sources.sellerAgentCommission,
    },
  ];
  return renderCitation(sources, "field-jurisdictionId");
}

/**
 * "Never sell" toggle (REN-13). The default framing subtracts a hypothetical
 * selling-agent commission from the owner's net worth at every point on the
 * curve, which is honest for the "what if I sold today" comparison but wrong
 * for a forever-home buyer. This checkbox opts into the indefinite-hold
 * framing without changing any other assumption.
 */
function renderNeverSellToggle(
  state: ScenarioInputs,
  onChange: () => void,
): HTMLElement {
  const wrapper = document.createElement("div");
  wrapper.className = "field field-checkbox";

  const label = document.createElement("label");
  label.htmlFor = "field-neverSell";

  const input = document.createElement("input");
  input.type = "checkbox";
  input.id = "field-neverSell";
  input.name = "neverSell";
  input.checked = state.neverSell;
  input.addEventListener("change", () => {
    state.neverSell = input.checked;
    onChange();
  });

  const labelText = document.createElement("span");
  labelText.className = "field-checkbox-label";
  labelText.textContent = "I plan to hold indefinitely (skip selling costs)";

  label.append(input, labelText);
  wrapper.append(label);

  const help = document.createElement("span");
  help.className = "field-note";
  help.textContent =
    "Off: subtracts a hypothetical selling-agent commission at every point on the chart (\"what if I sold today?\"). On: leaves that friction out — the honest framing for a forever home.";
  wrapper.append(help);

  return wrapper;
}

function renderActions(onReset: () => void): HTMLElement {
  const actions = document.createElement("div");
  actions.className = "actions";

  const shareButton = document.createElement("button");
  shareButton.type = "button";
  shareButton.className = "share-button";
  shareButton.textContent = "Copy shareable link";

  const shareStatus = document.createElement("span");
  shareStatus.className = "share-status";
  shareStatus.setAttribute("role", "status");
  shareStatus.setAttribute("aria-live", "polite");

  shareButton.addEventListener("click", async () => {
    if (typeof window === "undefined") return;
    const url = window.location.href;
    trackEvent("share");
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        shareStatus.textContent = "Link copied.";
      } else {
        shareStatus.textContent = url;
      }
    } catch {
      shareStatus.textContent = url;
    }
  });

  const resetButton = document.createElement("button");
  resetButton.type = "button";
  resetButton.className = "reset-button";
  resetButton.textContent = "Reset to defaults";
  resetButton.addEventListener("click", onReset);

  actions.append(shareButton, resetButton, shareStatus);
  return actions;
}

function renderFootnote(): HTMLElement {
  const note = document.createElement("p");
  note.className = "footnote";
  note.textContent =
    "Every number here is editable and every default is cited. Your inputs never leave your browser — the scenario travels in the URL, not on our servers.";
  return note;
}
